import React, { useState, useRef, useEffect } from "react";
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, isWithinInterval, isBefore, isAfter } from "date-fns";
import { vi } from "date-fns/locale";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const PRESETS = [
  { label: "Hôm nay", days: 0 },
  { label: "7 ngày", days: 7 },
  { label: "30 ngày", days: 30 },
  { label: "90 ngày", days: 90 },
];

export default function DateRangePicker({ onChange, initialDays = 7, storageKey = null }) {
  // Read persisted preset from localStorage
  const getInitialPresetDays = () => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw !== null) {
          const d = parseInt(raw, 10);
          if (!isNaN(d)) return d;
        }
      } catch {}
    }
    return initialDays;
  };

  const initPresetDays = getInitialPresetDays();
  const initStart = initPresetDays === 0
    ? startOfDay(new Date())
    : subDays(new Date(), initPresetDays);

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(initStart);
  const [endDate, setEndDate] = useState(new Date());
  const [selecting, setSelecting] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [activePreset, setActivePreset] = useState(
    PRESETS.find((p) => p.days === initPresetDays)?.label || null
  );
  const ref = useRef(null);
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const applyRange = (start, end, presetDays = null) => {
    setStartDate(start);
    setEndDate(end);
    setSelecting(null);
    setOpen(false);
    // Persist preset to localStorage
    if (storageKey) {
      if (presetDays !== null && presetDays !== undefined) {
        localStorage.setItem(storageKey, String(presetDays));
      } else {
        localStorage.removeItem(storageKey);
      }
    }
    if (onChange) onChange({ startDate: startOfDay(start), endDate: endOfDay(end), presetDays });
  };

  const handlePreset = (preset) => {
    const end = new Date();
    const start = subDays(end, preset.days);
    setActivePreset(preset.label);
    applyRange(start, end, preset.days);
  };

  const handleDayClick = (day) => {
    if (!selecting || selecting === "start") {
      setStartDate(day);
      setSelecting("end");
      setActivePreset(null);
    } else {
      // selecting === "end": always complete the range
      const [rangeStart, rangeEnd] = isBefore(day, startDate)
        ? [day, startDate]
        : [startDate, day];
      setActivePreset(null);
      applyRange(rangeStart, rangeEnd);
    }
  };

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = monthStart.getDay(); // 0=Sun

  const isInRange = (day) => {
    if (selecting === "end" && hoverDate) {
      const rangeEnd = isAfter(hoverDate, startDate) ? hoverDate : startDate;
      const rangeStart = isBefore(hoverDate, startDate) ? hoverDate : startDate;
      return isWithinInterval(day, { start: rangeStart, end: rangeEnd });
    }
    if (startDate && endDate) {
      return isWithinInterval(day, { start: startDate, end: endDate });
    }
    return false;
  };

  const displayText = `${format(startDate, "dd/MM/yyyy")} – ${format(endDate, "dd/MM/yyyy")}`;
  const shortDisplayText = `${format(startDate, "dd/MM")} – ${format(endDate, "dd/MM")}`;

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => handlePreset(p)}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activePreset === p.label
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-600 hover:bg-gray-100 border"
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => {
            if (open) {
              setOpen(false);
              setSelecting(null);
            } else {
              setOpen(true);
              setSelecting("start");
              setViewMonth(startDate); // navigate calendar to current selection
            }
          }}
          className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
            open ? "bg-primary-50 border-primary-300 text-primary-700" : "bg-white text-gray-600 hover:bg-gray-100"
          }`}
        >
          <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="hidden sm:inline">{displayText}</span>
          <span className="inline sm:hidden">{shortDisplayText}</span>
        </button>
      </div>

      {/* Dropdown calendar */}
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border p-4 z-50 w-80 max-w-[calc(100vw-1.5rem)]">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setViewMonth(subMonths(viewMonth, 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-800">
              {format(viewMonth, "MMMM yyyy", { locale: vi })}
            </span>
            <button
              onClick={() => setViewMonth(addMonths(viewMonth, 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {["CN", "T2", "T3", "T4", "T5", "T6", "T7"].map((d) => (
              <div key={d} className="text-xs font-medium text-gray-400 py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startPad }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {days.map((day) => {
              const isStart = isSameDay(day, startDate);
              const isEnd = isSameDay(day, endDate);
              const inRange = isInRange(day);
              const isToday = isSameDay(day, new Date());
              const isFuture = isAfter(day, new Date());

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => !isFuture && handleDayClick(day)}
                  onMouseEnter={() => selecting === "end" && setHoverDate(day)}
                  disabled={isFuture}
                  className={`
                    h-8 w-full text-xs rounded-md transition-colors
                    ${isFuture ? "text-gray-300 cursor-not-allowed" : "hover:bg-primary-50 cursor-pointer"}
                    ${isStart || isEnd ? "bg-primary-600 text-white font-bold" : ""}
                    ${inRange && !isStart && !isEnd ? "bg-primary-100 text-primary-800" : ""}
                    ${isToday && !isStart && !isEnd ? "ring-1 ring-primary-400" : ""}
                    ${!inRange && !isStart && !isEnd && !isFuture ? "text-gray-700" : ""}
                  `}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>

          {/* Selection hint */}
          <div className="mt-3 text-xs text-gray-400 text-center">
            {selecting === "start" ? "Chọn ngày bắt đầu" : selecting === "end" ? "Chọn ngày kết thúc" : displayText}
          </div>
        </div>
      )}
    </div>
  );
}
